import { SelectOptionType } from '@octano/global-ui';
import { ReactElement, useCallback } from 'react';
import { FieldValues } from 'react-hook-form';
import { AxiosResultDefaultError } from '../../../../../../../api/request';
import { AuthenticationError } from '../../../../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../../../components/info/DisplayError';
import Loading from '../../../../../../../components/info/Loading';
import { useParameters } from '../../../../../../../hooks/useParameters';
import { getSelectedOption } from '../../../../../../../utils/selectFormat';
import { getFormData } from '../../api';
import { PersonalBackgroundFormDataResponse } from '../../type';

interface BaseLoaderDataProps extends PersonalBackgroundFormDataResponse {
  campusesOptions: SelectOptionType[];
  schoolsOptions: SelectOptionType[];
}

interface BaseLoaderProps {
  teacherId: number;
  children: ({
    defaultValues,
    data,
  }: {
    defaultValues: FieldValues;
    data: BaseLoaderDataProps;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  PersonalBackgroundFormDataResponse,
  AuthenticationError | AxiosResultDefaultError
>();

export function usePersonalBackgroundFormDataLoader() {
  return useFetch();
}

export const personalBackgroundFormDataLoaderConsumer = FetchConsumer;

export default function PersonalBackgroundFormDataLoader({
  teacherId,
  children,
}: BaseLoaderProps) {
  const request = useCallback(async () => {
    return getFormData(teacherId);
  }, [teacherId]);

  const { countryOptions } = useParameters();

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading) {
            return <Loading insideCard />;
          } else if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
              />
            );
          } else if (data) {
            const selectOptions = {
              schoolsOptions: data.schools.map((row) => ({
                value: row.id,
                label: row.name,
              })),
              campusesOptions: data.campuses.map((row) => ({
                value: row.id,
                label: row.name,
              })),
            };

            return children({
              defaultValues: {
                ...data?.responses,
                passportCountry: data?.responses?.passportCountry
                  ? getSelectedOption(
                      data?.responses?.passportCountry?.id,
                      countryOptions,
                    )
                  : undefined,
                campusId: data?.responses?.campusId
                  ? getSelectedOption(
                      data?.responses?.campusId,
                      selectOptions.campusesOptions,
                    )
                  : undefined,
                schoolId: data?.responses?.schoolId
                  ? getSelectedOption(
                      data?.responses?.schoolId,
                      selectOptions.schoolsOptions,
                    )
                  : undefined,
              },
              data: {
                ...data,
                ...selectOptions,
              },
            });
          } else {
            return null;
          }
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
